import { useEffect, useRef, useState } from 'react'

import { getAIConversation, getAIConversations } from '../../../../api'
import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../../constants/common'
import { useApi } from '../../../../hooks'
import { isApiNotFoundStatus } from '../../../../utils/api'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import { getGretaAIChatErrorStatus } from '../../GretaAIChat.utils'

import {
  GRETA_AI_CHAT_CHATS_INITIAL_PAGING,
  GRETA_AI_CHAT_CHATS_PARAMS,
  GRETA_AI_CHAT_CHATS_POLL_MS,
} from './useGretaAIChatChatListLoader.constants'
import {
  getHasGretaAIChatChatId,
  getHasNextGretaAIChatChatsPage,
  mapGretaAIChatChats,
  mergeGretaAIChatChats,
} from './useGretaAIChatChatListLoader.utils'
import { useGretaAIChatChatMutations } from './useGretaAIChatChatMutations'
import { useGretaAIChatSettledGenerations } from './useGretaAIChatSettledGenerations'

export function useGretaAIChatChatListLoader({
  activeChatId = null,
  isMinimized = false,
  isOpen = false,
  onActiveChatMissing = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const [chats, setChats] = useState(EMPTY_LIST)
  const [chatsStatus, setChatsStatus] = useState(GretaAIChatStatus.Loading)
  const [paging, setPaging] = useState(GRETA_AI_CHAT_CHATS_INITIAL_PAGING)
  const [isMoreChatsLoading, setIsMoreChatsLoading] = useState(false)
  const [hasMoreChatsFailed, setHasMoreChatsFailed] = useState(false)

  const hasLoadedRef = useRef(false)
  const isLoadingMoreRef = useRef(false)
  const refreshChatsRef = useRef(EMPTY_FUNCTION)

  const chatsApi = useApi()
  const moreChatsApi = useApi()
  const refreshApi = useApi()

  const {
    getIsChatGenerationSettledRecently,
    hideStaleRunningChats,
    markChatGenerationSettled,
  } = useGretaAIChatSettledGenerations({ setChats, onRecheck: refreshChats })

  const { addChat, dropChat, removeChat, renameChat } =
    useGretaAIChatChatMutations({ chats, setChats, setChatsStatus })

  async function reconcileRestoredSelection(loadedChats, options) {
    if (activeChatId && !getHasGretaAIChatChatId(loadedChats, activeChatId)) {
      try {
        await getAIConversation({ id: activeChatId, options })
      } catch (reason) {
        if (isApiNotFoundStatus(reason?.response?.status)) {
          onActiveChatMissing(activeChatId)
        }
      }
    }
  }

  function mapLoadedChats(response, requestedAt) {
    return hideStaleRunningChats(mapGretaAIChatChats(response), requestedAt)
  }

  async function fetchChats({ options }) {
    setChatsStatus(GretaAIChatStatus.Loading)

    const requestedAt = performance.now()

    try {
      const response = await getAIConversations({
        options,
        params: GRETA_AI_CHAT_CHATS_PARAMS,
      })

      if (response) {
        const loadedChats = mapLoadedChats(response, requestedAt)

        setChats(current => mergeGretaAIChatChats(loadedChats, current))
        setPaging({
          hasMore: getHasNextGretaAIChatChatsPage(response),
          page: GRETA_AI_CHAT_CHATS_PARAMS.page,
        })
        setChatsStatus(GretaAIChatStatus.Idle)
        await reconcileRestoredSelection(loadedChats, options)
      }
    } catch (reason) {
      if (!options?.signal?.aborted) {
        const errorStatus = await getGretaAIChatErrorStatus(reason)

        if (!options?.signal?.aborted) {
          setChatsStatus(errorStatus)
        }
      }
    }
  }

  async function fetchMoreChats({ options }) {
    const nextPage = paging.page + 1
    const requestedAt = performance.now()

    try {
      const response = await getAIConversations({
        options,
        params: { ...GRETA_AI_CHAT_CHATS_PARAMS, page: nextPage },
      })

      if (response) {
        const loadedChats = mapLoadedChats(response, requestedAt)

        setChats(current => mergeGretaAIChatChats(current, loadedChats))
        setPaging({
          hasMore: getHasNextGretaAIChatChatsPage(response),
          page: nextPage,
        })
      }
    } catch {
      setHasMoreChatsFailed(true)
    } finally {
      isLoadingMoreRef.current = false
      setIsMoreChatsLoading(false)
    }
  }

  async function fetchRefreshedChats({ options }) {
    const requestedAt = performance.now()

    try {
      const response = await getAIConversations({
        options,
        params: GRETA_AI_CHAT_CHATS_PARAMS,
      })

      if (response) {
        const loadedChats = mapLoadedChats(response, requestedAt)

        setChats(current => mergeGretaAIChatChats(loadedChats, current))
        // Keep the pagination cursor: a refresh only re-pulls page 1, so overwriting
        // the cursor would re-fetch already-loaded pages and stall infinite scroll.
        setPaging(current => ({
          hasMore:
            current.page > GRETA_AI_CHAT_CHATS_PARAMS.page
              ? current.hasMore
              : getHasNextGretaAIChatChatsPage(response),
          page: current.page,
        }))
        setChatsStatus(GretaAIChatStatus.Idle)
      }
    } catch {
      // A background refresh stays silent on failure.
    }
  }

  function loadChats() {
    chatsApi.call(fetchChats)
  }

  // Ref, not state: a scroll burst re-fires this before React re-renders, so a flag reads stale.
  function loadMoreChats() {
    if (paging.hasMore && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true
      setIsMoreChatsLoading(true)
      setHasMoreChatsFailed(false)
      moreChatsApi.call(fetchMoreChats)
    }
  }

  function refreshChats() {
    refreshApi.call(fetchRefreshedChats)
  }

  // Unconfirmed (a stop the server never answered): the turn may still run, so nothing is hidden.
  function settleChatGeneration(id, { isConfirmed = true } = EMPTY_OBJECT) {
    if (isConfirmed) {
      markChatGenerationSettled(id)
    }

    refreshChats()
  }

  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadChats()
    }
  }, [isOpen])

  useEffect(() => {
    refreshChatsRef.current = refreshChats
  })

  useEffect(() => {
    if (isOpen && !isMinimized) {
      const intervalId = window.setInterval(
        () => refreshChatsRef.current(),
        GRETA_AI_CHAT_CHATS_POLL_MS
      )

      return () => window.clearInterval(intervalId)
    }
  }, [isMinimized, isOpen])

  return {
    addChat,
    chats,
    chatsStatus,
    dropChat,
    getIsChatGenerationSettledRecently,
    hasMoreChats: paging.hasMore,
    hasMoreChatsFailed,
    isMoreChatsLoading,
    loadChats,
    loadMoreChats,
    refreshChats,
    removeChat,
    renameChat,
    settleChatGeneration,
  }
}
